import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../../api/client'
import type { Progress } from '../plans/api'

export const EVENT_STATUSES = ['Planning', 'Active', 'Complete', 'Cancelled'] as const
export type EventStatus = (typeof EVENT_STATUSES)[number]

export interface EventListItem {
  id: string
  name: string
  code: string
  startDate: string
  endDate: string
  venueName: string | null
  venueCity: string | null
  status: EventStatus
  isArchived: boolean
  accountManagerUserId: string
  accountManagerDisplayName: string | null
  /** True when the named account manager is no longer an active user. */
  needsAccountManager: boolean
  hasPlan: boolean
  /**
   * The event plan's progress, or null when the event has no plan. A plan with
   * no items reports a total of zero rather than null: "nothing here" and "no
   * plan at all" are different answers.
   */
  progress: Progress | null
}

// The detail endpoint returns everything the list row has except the progress
// summary - the plan itself is fetched alongside it, and carries its own.
export interface EventDetail extends Omit<EventListItem, 'progress'> {
  accountManagerEmail: string | null
  venueTimeZoneId: string | null
  notes: string | null
  externalCustomRegId: string | null
  createdAt: string
  updatedAt: string | null
}

export interface EventFormValues {
  name: string
  code: string
  startDate: string
  endDate: string
  accountManagerUserId: string
  venueName: string | null
  venueCity: string | null
  venueTimeZoneId: string | null
  notes: string | null
  externalCustomRegId: string | null
}

export interface EventFilters {
  search?: string
  status?: EventStatus | ''
  includeArchived?: boolean
  needsAccountManager?: boolean
}

const eventsKey = ['events'] as const
const eventKey = (id: string) => ['events', id] as const

function toQueryString(filters: EventFilters): string {
  const params = new URLSearchParams()
  if (filters.search) params.set('search', filters.search)
  if (filters.status) params.set('status', filters.status)
  if (filters.includeArchived) params.set('includeArchived', 'true')
  if (filters.needsAccountManager) params.set('needsAccountManager', 'true')
  const query = params.toString()
  return query ? `?${query}` : ''
}

export function useEvents(filters: EventFilters) {
  return useQuery({
    queryKey: [...eventsKey, filters],
    queryFn: () => apiFetch<EventListItem[]>(`/api/events${toQueryString(filters)}`),
    // Keep the previous page visible while a new search runs, so typing does
    // not flash the list empty on every keystroke.
    placeholderData: (previous) => previous,
  })
}

export function useEvent(id: string | undefined) {
  return useQuery({
    queryKey: eventKey(id ?? ''),
    queryFn: () => apiFetch<EventDetail>(`/api/events/${id}`),
    enabled: Boolean(id),
  })
}

export function useCreateEvent() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (values: EventFormValues) =>
      apiFetch<EventDetail>('/api/events', { method: 'POST', body: values }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: eventsKey }),
  })
}

export function useUpdateEvent(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (values: EventFormValues & { confirmPlanRecalculation?: boolean }) =>
      apiFetch<EventDetail>(`/api/events/${id}`, { method: 'PUT', body: values }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: eventsKey }),
  })
}

export function useChangeEventStatus(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (status: EventStatus) =>
      apiFetch<EventDetail>(`/api/events/${id}/status`, { method: 'PUT', body: { status } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: eventsKey }),
  })
}

export function useSetEventArchived(id: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (archived: boolean) =>
      apiFetch<void>(`/api/events/${id}/${archived ? 'archive' : 'unarchive'}`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: eventsKey }),
  })
}

/**
 * Formats an event's date range the way the team reads it off a schedule.
 *
 * Dates arrive as plain calendar days ("2026-06-01"). They are parsed as local
 * midnight rather than UTC, because parsing them as UTC renders the day before
 * anywhere behind UTC - the off-by-one the domain model deliberately avoids.
 */
const fullDate = (date: Date) =>
  date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

export function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(`${startDate}T00:00:00`)
  const end = new Date(`${endDate}T00:00:00`)

  if (startDate === endDate) {
    return fullDate(start)
  }

  const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()

  if (sameMonth) {
    const month = start.toLocaleDateString(undefined, { month: 'short' })
    return `${month} ${start.getDate()} – ${end.getDate()}, ${start.getFullYear()}`
  }

  return `${fullDate(start)} – ${fullDate(end)}`
}
